import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import EventFormDialog from '../../components/admin/EventFormDialog';
import {
  ConfirmDialog,
  DataTable,
  InlineAlert,
  LoadingState,
  PageHeader,
  Toast,
} from '../../components/admin/ui';

const EventsManagement = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const fetchEvents = async () => {
    try {
      const response = await api.get('/admin/events');
      setEvents(response.data.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/events/${confirmId}`);
      setConfirmId(null);
      setToast({ open: true, message: 'Evento eliminado', tone: 'success' });
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      setToast({ open: true, message: 'Error al eliminar el evento', tone: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState />;

  const columns = [
    {
      key: 'type',
      header: 'Tipo',
      render: (row) => (
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            row.event_type === 'exam' ? 'bg-primary/10 text-primary' : 'bg-ink text-white'
          }`}
        >
          {row.event_type === 'exam' ? 'Examen' : 'Pelea'}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Título',
      render: (row) => <span className="font-semibold">{row.title}</span>,
    },
    {
      key: 'event_date',
      header: 'Fecha',
      render: (row) =>
        new Date(row.event_date).toLocaleDateString('es-AR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
    },
    {
      key: 'description',
      header: 'Descripción',
      render: (row) => <span className="line-clamp-2 max-w-xs text-muted">{row.description || '—'}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            className="btn-admin-ghost !min-h-9"
            onClick={() => {
              setSelectedEvent(row);
              setDialogOpen(true);
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn-admin-ghost !min-h-9 text-danger"
            onClick={() => setConfirmId(row.id)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <PageHeader
        title="Eventos"
        onBack={() => navigate('/admin')}
        action={
          <button
            type="button"
            className="btn-admin"
            onClick={() => {
              setSelectedEvent(null);
              setDialogOpen(true);
            }}
          >
            Crear
          </button>
        }
      />

      <DataTable columns={columns} rows={events} emptyTitle="No hay eventos registrados" />

      <InlineAlert tone="info" className="mt-6">
        La gestión de participantes y galerías se implementará en una fase posterior.
      </InlineAlert>

      <EventFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onSuccess={fetchEvents}
      />

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar evento"
        message="¿Estás seguro de eliminar este evento?"
        confirmLabel="Eliminar"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default EventsManagement;
